import { useState } from 'react'
import { createPortal } from 'react-dom'

import Link from 'ageas-react-component-link'
import Section from 'ageas-react-component-section'
import TextField from 'ageas-react-component-textfield'

import { useAnimalValidateStore } from '../../Stores/useAnimalValidateStore'
import { useGenerateProposalDocumentsStore } from '../../Stores/useGenerateProposalDocumentsStore'
import { QuestionsBlock } from '../../Types/AnimalStore'
import { OPTIONS_YES_NO } from '../../constants/optionsYesNo'
import { t } from '../../i18n'
import { RadioButtonContainer } from '../OwnerData/style'
import { RadioSectionContainer } from '../SummaryConditions/styles'
import { CreateAddress } from './CreateAddress'
import { Radio } from './Radio'

type Props = {
  declaration: QuestionsBlock
  animalId: string
  disabled?: boolean
  changeValue: (
    declarationId: number | string,
    itemId: number | string,
    value: string | null,
    isOwnerQuestion?: boolean,
    affectedQuestionCode?: string,
  ) => void
  validationQuiz: (value: any) => void
}

const elementPortal = document.getElementsByTagName('body')?.[0]

export const OwnerQuestionary = ({
  declaration,
  changeValue,
  animalId,
  validationQuiz,
  disabled,
}: Props) => {
  const [showCreateAddress, setShowCreateAddress] = useState(false)
  const [resetStateCreateAddress, setResetStateCreateAddress] = useState(false)
  const { resetAddressOwner } = useAnimalValidateStore()
  const { isGenerateProposalDocuments } = useGenerateProposalDocumentsStore()

  const questionMOR = declaration?.questions?.find(
    (question) => (question.conditions || [])?.length > 0,
  )

  const [isOwner, setIsOwner] = useState<string | null>(
    questionMOR?.answer as never,
  )

  const questionPM = declaration?.questions?.find((question) =>
    questionMOR?.conditions
      ?.map((item) => item.affectedQuestionCode)
      .includes(question.id as string),
  )

  const conditionPM = questionMOR?.conditions?.find(
    (condition) => condition.affectedQuestionCode === questionPM?.id,
  )

  const handleCreateAddress = (
    declarationId: number | string,
    itemId: number | string,
    value: string,
  ) => {
    changeValue(declarationId, itemId, value)
    validationQuiz({
      [`${animalId}-${declaration.id}-${questionPM?.id}`]:
        value?.length > 0 ? value : null,
    })
  }

  const handleOwnerChangeValue = (value: string) => {
    setIsOwner(value)
    changeValue(
      declaration.id,
      questionMOR?.id as never,
      value,
      true,
      questionPM?.id as never,
    )

    if (value === OPTIONS_YES_NO.yes) {
      validationQuiz({
        [`${animalId}-${declaration.id}-${questionMOR?.id}`]: value,
        [`${animalId}-${declaration.id}-${questionPM?.id}`]: OPTIONS_YES_NO.yes,
      })

      if ((questionPM?.answer || '')?.length > 0) {
        resetAddressOwner(`address-${animalId}`)
        setResetStateCreateAddress(true)
      }
    } else {
      validationQuiz({
        [`${animalId}-${declaration.id}-${questionPM?.id}`]: null,
        [`${animalId}-${declaration.id}-${questionMOR?.id}`]: OPTIONS_YES_NO.no,
      })
    }
  }

  return (
    <>
      <RadioSectionContainer key={declaration.id}>
        <Section
          id="healthSection"
          name={declaration.title}
          config={{ labelSize: '20px', labelBold: '500' }}
          colorTheme="blue"
        />
        <RadioButtonContainer>
          <Radio
            isMandatory
            item={questionMOR as never}
            readOnly={isGenerateProposalDocuments || disabled}
            changeValue={(value: string) => {
              if (!isGenerateProposalDocuments) {
                handleOwnerChangeValue(value)
              }
            }}
          />
          {!showCreateAddress && (questionPM?.answer || '')?.length > 0 && (
            <TextField
              readOnly
              id={`owner-address-${animalId}`}
              name={`owner-address-${animalId}`}
              label={t('forms.ownerAddress')}
              colorTheme="blue"
              value={questionPM?.answer || ''}
              version="2"
            />
          )}
          {!isGenerateProposalDocuments && !disabled && (
            <>
              {isOwner?.charAt(0).toLowerCase() ===
                conditionPM?.answerCode?.toLowerCase() && (
                <div className="add-address">
                  <Link
                    label={
                      (questionPM?.answer || '')?.length > 0
                        ? t('forms.updateOwnerAddressLabel')
                        : questionPM?.question + '*'
                    }
                    onValueChanged={() => setShowCreateAddress(true)}
                  />
                </div>
              )}
            </>
          )}
        </RadioButtonContainer>
      </RadioSectionContainer>
      {createPortal(
        <CreateAddress
          isVisible={showCreateAddress}
          animalId={animalId}
          questionPM={questionPM as never}
          declaration={declaration}
          changeValue={handleCreateAddress}
          isResetState={resetStateCreateAddress}
          closeAction={() => setShowCreateAddress(false)}
        />,
        elementPortal!,
      )}
    </>
  )
}
